<template>
  <div>
    <RegisterMultipleDevicesDialog
      :open="open"
      @close="onCloseHandler"
      :step="step"
      :title="translateString(currentStepConfiguration.overrideTitle)"
      :subTitle="translateString(currentStepConfiguration.overrideSubtitle)"
      :downloadCsv="currentStepConfiguration.downloadCsv"
      :disableActionButton="currentStepConfiguration.disableActionButton"
      :onClickActionButton="actionHandler"
      :actionButtonText="translateString(currentStepConfiguration.overrideActionButtonText)"
      :cssStyle="currentStepConfiguration.cssStyle"
      :removeCancelButton="currentStepConfiguration.removeCancelButton"
    >
      <template v-slot:content>
        <v-form v-if="currentStepConfiguration.process === stepProcesses.UPLOAD">
          <FileCsvInput @onFileChanged="setCsvFile" :model-value="csvFile" />
        </v-form>
        <InProgressMessage
          v-if="currentStepConfiguration.process === stepProcesses.PROGRESS"
          :messages="progressMessages"
        />
        <SuccessMessage
          v-if="currentStepConfiguration.process === stepProcesses.BEFORE_REGISTER"
          :message="translateString('registerMultipleDevices.uploadSucceeded')"
          :fileName="csvFile?.name || ''"
        />
        <ErrorMessage
          v-if="currentStepConfiguration.process === stepProcesses.FAILED"
          :errors="csvErrors"
        />
        <SummaryMessage
          v-if="currentStepConfiguration.process === stepProcesses.AFTER_REGISTER"
          :errors="registrationErrors"
          :registeredDevices="registeredDevices"
          :failedDevices="failedDevices"
          :notProcessedDevices="notProcessedDevices"
        />
      </template>
    </RegisterMultipleDevicesDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { RegisterDeviceRequest, StateItemPoint, UpdateDeviceRequest } from '@/types/devices'
import { DEVICE_DEFAULT_CONFIG } from '@/services/device-config'
import { DEVICE_ACTIONS, DEVICE_GETTERS, type SetDevicePayload } from '@/store/devices'
import { SUBSCRIPTIONS_ACTIONS, SUBSCRIPTIONS_GETTERS } from '@/store/subscriptions'
import { subscriptionGeneralTiers, subscriptionTypes } from '@/store/subscriptions/utils'
import { NAMESPACES } from '@/store/types'
import RegisterMultipleDevicesDialog from './dialog/registerMultipleDevicesDialog.vue'
import FileCsvInput from './messages/content/fileInput.vue'
import ErrorMessage from './messages/errorMessage.vue'
import SuccessMessage from './messages/successMessage.vue'
import InProgressMessage from './messages/inProgressMessage.vue'
import SummaryMessage from './messages/summaryMessage.vue'
import { formatDeviceData, formatRegisterError, sanitizeRows } from './helper'
import type { StepConfig } from './utils'
import { REGISTER_MULTIPLE_DEVICES_STEPS, STEP_NAMES, STEP_PROCESSES, CLOSE_ACTIONS } from './utils'
import { getCsvData, validateCsvRow, validateCsvFile, readCsvContent } from './validation/csv-utils'

interface RegistrationSummary {
  registeredDevices: number
  failedDevices: number
  notProcessedDevices: number
  errors: string[]
}

export default defineComponent({
  name: 'RegisterMultipleDevices',
  components: {
    RegisterMultipleDevicesDialog,
    FileCsvInput,
    ErrorMessage,
    SuccessMessage,
    InProgressMessage,
    SummaryMessage
  },
  props: {
    open: Boolean
  },
  emits: ['close'],
  data() {
    return {
      step: STEP_NAMES.UPLOAD_FILE as StepConfig['name'],
      csvFile: null as File | null,
      csvErrors: [] as string[] | string,
      stepProcesses: STEP_PROCESSES,
      message: '' as string | string[],
      registrationErrors: [] as string[],
      registeredDevices: 0,
      failedDevices: 0,
      notProcessedDevices: 0,
      cancelRegistration: false
    }
  },
  computed: {
    currentStepConfiguration(): Partial<StepConfig> {
      return REGISTER_MULTIPLE_DEVICES_STEPS[this.step] || {}
    },
    progressMessages(): string[] {
      if (Array.isArray(this.message)) {
        return this.message
      }

      return this.message ? [this.message] : []
    }
  },
  methods: {
    translateString(key?: string): string | undefined {
      return key ? String(this.$t(key)) : undefined
    },
    onCloseHandler(): void {
      if (this.currentStepConfiguration.closeAction === CLOSE_ACTIONS.STOP_REGISTRATION) {
        this.stopRegistration()
      } else {
        this.close()
      }
    },
    close(): void {
      this.$emit('close')
      setTimeout(() => {
        this.init()
      }, 500)
    },
    init(): void {
      this.step = STEP_NAMES.UPLOAD_FILE
      this.csvFile = null
      this.csvErrors = []
      this.message = ''
      this.registrationErrors = []
      this.registeredDevices = 0
      this.failedDevices = 0
      this.notProcessedDevices = 0
      this.cancelRegistration = false
    },
    async actionHandler(): Promise<void> {
      switch (this.step) {
        case STEP_NAMES.UPLOAD_FAILED:
        case STEP_NAMES.SHOW_CSV_ERRORS:
          this.retry()
          break
        case STEP_NAMES.UPLOAD_SUCCEEDED:
          await this.startRegistration()
          break
        case STEP_NAMES.SHOW_REGISTRATION_RESULT:
          this.onCloseHandler()
          break
      }
    },
    async setCsvFile(file: File | null): Promise<void> {
      this.csvFile = file
      await this.validateCsv()
    },
    retry(): void {
      this.init()
    },
    async validateCsv(): Promise<void> {
      if (this.csvFile && this.csvFile.type === 'text/csv') {
        this.message = [String(this.$t('registerMultipleDevices.checkingForErrors'))]
        this.step = STEP_NAMES.VALIDATE_CSV
        let errors: string[] | string = []
        const csvContent = await readCsvContent(this.csvFile)
        const fileError = await validateCsvFile(csvContent)

        if (fileError === '') {
          const csvData = await getCsvData(csvContent)
          csvData.forEach((dataRow) => {
            const dataErrors = validateCsvRow(dataRow)
            errors = Array.isArray(errors) ? errors.concat(dataErrors) : dataErrors
          })
        } else {
          errors = fileError
        }

        if (errors.length > 0) {
          this.csvErrors = errors
          this.step = STEP_NAMES.SHOW_CSV_ERRORS
        } else {
          this.step = STEP_NAMES.UPLOAD_SUCCEEDED
        }
      } else {
        this.csvErrors = String(this.$t('registerMultipleDevices.incorrectFileType'))
        this.step = STEP_NAMES.UPLOAD_FAILED
      }
    },
    async startRegistration(): Promise<void> {
      const devices = await this.prepareRegistration()
      const registerData = await this.registerDevices(devices)
      this.prepareRegistrationSummary(registerData)
      this.showRegistrationSummary()
    },
    async prepareRegistration(): Promise<ReturnType<typeof sanitizeRows>> {
      this.step = STEP_NAMES.REGISTER_DEVICES
      const csvContent = await readCsvContent(this.csvFile as File)
      return sanitizeRows(await getCsvData(csvContent))
    },
    async registerDevice(deviceData: RegisterDeviceRequest): Promise<boolean> {
      let success = false

      const resp = await this.$api.postRegisterDevice(deviceData)
      if (resp.ok) {
        success = true
        const body = await resp.json()
        const location = body.cloud_state_data?.location?.value as StateItemPoint | undefined
        const setData: SetDevicePayload = {
          accountId: body.cloud_account_id,
          deviceId: body.cloud_device_id,
          model: body.model,
          submodel: undefined,
          serial: body.serial,
          name: body.cloud_state_data?.name?.value as string,
          locationName: body.cloud_state_data?.location_name?.value as string,
          indoor: body.cloud_state_data?.is_indoor?.value as boolean,
          isPublic: body.cloud_state_data?.is_public?.value as boolean,
          coords: {
            lng: location?.coordinates?.[0],
            lat: location?.coordinates?.[1]
          }
        }
        this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_DEVICE}`, setData)
        await this.updateDeviceConfig(body.cloud_device_id)
      } else {
        const message = await resp.text()
        throw new Error(message)
      }

      return success
    },
    async updateDevice(deviceData: RegisterDeviceRequest, deviceId: string): Promise<boolean> {
      let success = false

      const patchSubPayload = {
        model: deviceData.model,
        serial: deviceData.serial,
        subscription: this.getPatchDeviceSubscription(deviceData.model),
        isExternal: false
      }

      const patchSub = await this.$api.patchDeviceSubscription(patchSubPayload)

      if (patchSub.ok) {
        const updateDevice = await this.$api.patchUpdateDevice(
          deviceData as UpdateDeviceRequest,
          deviceId
        )
        if (updateDevice.ok) {
          success = true
        } else {
          await this.$api.patchDeviceSubscription({
            ...patchSubPayload,
            delete: true
          })
        }
      } else {
        const message = await patchSub.text()
        throw new Error(message)
      }

      if (success) {
        await this.updateDeviceConfig(deviceId)
      }

      return success
    },
    async updateDeviceConfig(deviceId: string): Promise<void> {
      await this.$api.postDeviceConfig(DEVICE_DEFAULT_CONFIG, deviceId)
    },
    async registerDevices(devices: ReturnType<typeof sanitizeRows>): Promise<RegistrationSummary> {
      const registerData: RegistrationSummary = {
        registeredDevices: 0,
        failedDevices: 0,
        notProcessedDevices: 0,
        errors: []
      }

      this.message = this.getInProgressMessages(0, devices.length)

      for (const [index, device] of devices.entries()) {
        if (this.cancelRegistration) {
          break
        }

        this.message = this.getInProgressMessages(index + 1, devices.length)
        const deviceData = formatDeviceData(device)
        deviceData.subscription = this.getDeviceSubscription(deviceData.model)

        try {
          let success = false
          if (this.hasDataServices(deviceData.serial)) {
            const deviceId = this.getDeviceId(deviceData.serial)
            success = await this.updateDevice(deviceData, deviceId)
          } else {
            success = await this.registerDevice(deviceData)
          }
          if (success) {
            registerData.registeredDevices++
          }
        } catch (error) {
          registerData.failedDevices++
          registerData.errors.push(formatRegisterError(String(error), deviceData.serial))
        }
      }

      registerData.notProcessedDevices =
        devices.length - (registerData.registeredDevices + registerData.failedDevices)

      try {
        await this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_ALL_USER_DEVICES}`)
        await this.$store.dispatch(
          `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_ACTIONS.UPDATE_SUBSCRIPTIONS}`,
          {
            auth: this.$auth,
            api: this.$api
          }
        )
        await this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_ALL_DEVICES}`)
      } catch {
        // ignore, stop the spinner animation.
      }

      return registerData
    },
    stopRegistration(): void {
      this.cancelRegistration = true
    },
    prepareRegistrationSummary(registerData: RegistrationSummary): void {
      this.registeredDevices = registerData.registeredDevices
      this.failedDevices = registerData.failedDevices
      this.notProcessedDevices = registerData.notProcessedDevices
      this.registrationErrors = registerData.errors
    },
    showRegistrationSummary(): void {
      this.step = STEP_NAMES.SHOW_REGISTRATION_RESULT
    },
    getInProgressMessages(processedDevices: number, numberOfDevices: number): string[] {
      return [
        String(this.$t('registerMultipleDevices.registeringDevices')),
        String(this.$t('registerMultipleDevices.registerProgress'))
          .replace('/registeredDevices/', String(processedDevices))
          .replace('/numberOfDevices/', String(numberOfDevices))
      ]
    },
    canAddToBasicAndPremiumSubscriptions(modelNumber: string): boolean {
      return (
        this.$store.getters[
          `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_BASIC_BY_MODEL}`
        ](modelNumber) &&
        this.$store.getters[
          `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.CAN_ADD_DEVICE_TO_PREMIUM_BY_MODEL}`
        ](modelNumber)
      )
    },
    hasDataServices(serialNumber: string): boolean {
      return this.$store.getters[
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_GETTERS.GET_DEVICE_DATA_SERVICES_BY_DEVICE_SERIAL}`
      ](serialNumber) as boolean
    },
    getDeviceId(serialNumber: string): string {
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_DEVICE_ID_BY_DEVICE_SERIAL}`
      ](serialNumber) as string
    },
    getDeviceSubscription(modelNumber: string): string {
      return this.canAddToBasicAndPremiumSubscriptions(modelNumber)
        ? subscriptionGeneralTiers.PREMIUM
        : subscriptionTypes.WEBSITE
    },
    getPatchDeviceSubscription(modelNumber: string): string | null {
      return this.canAddToBasicAndPremiumSubscriptions(modelNumber)
        ? subscriptionGeneralTiers.PREMIUM
        : null
    }
  }
})
</script>
